<template>
  <div class="tip-detail-container">
    <LoadingSpinner v-if="loading" />
    <div v-else-if="tip" class="tip-detail">
      <div class="tip-card">
        <div class="tip-header">
          <router-link :to="`/${tip.user?.username || 'unknown'}`" class="tip-profile-link">
            <img :src="tip.user?.avatar || 'default-avatar.png'" alt="User Profile" class="tip-profile-pic" />
          </router-link>
          <div class="tip-user-details">
            <p class="tip-author">
              {{ tip.user?.username || 'Unknown' }}
              <img
                v-if="tip.user?.verified"
                src="@/assets/verified.png"
                alt="Verified"
                class="verified-badge"
                title="Verified Skier"
              />
            </p>
            <p class="tip-full-name">{{ tip.user?.first_name || '' }} {{ tip.user?.last_name || '' }}</p>
          </div>
          <span
            v-if="tip.pro_tip"
            class="pro-tip-badge"
            title="Pro Tip"
            @click="showProTipModal = true"
          >
            PRO TIP
          </span>
          <p class="tip-date">{{ formatDate(tip.created) }}</p>
          <i 
            v-if="tip.user?.id === userID && isAuthenticated" 
            class="fas fa-trash delete-icon" 
            @click="confirmDelete"
            title="Delete Tip"
          ></i>
        </div>
        
        <div class="tip-body-container">
          <div v-if="shouldBlurContent" class="tip-body">
            <span class="visible-text">{{ tip.body.substring(0, 200) }}</span>
            <span class="blurred-text">{{ tip.body.substring(200) }}</span>
          </div>
          <p v-else class="tip-body">{{ tip.body }}</p>
        </div>
        
        <div class="tip-actions">
          <div class="tip-rate">
            <i
              class="fas fa-minus-circle"
              :class="{ 'voted-down': tip.userVote === -1 }"
              @click="handleVote(-1)"
            ></i>
            <span>{{ tip.ratecount }}</span>
            <i
              class="fas fa-plus-circle"
              :class="{ 'voted-up': tip.userVote === 1 }"
              @click="handleVote(1)"
            ></i>
          </div>
          <p class="tip-replies" @click="toggleReplies">
            {{ tip.reply_count }} repl<span v-if="tip.reply_count !== 1">ies</span><span v-else>y</span>
          </p>
        </div>
      </div>

      <!-- Replies Section -->
      <div v-if="showReplies" class="replies-section">
        <div v-if="loadingReplies" class="loading-replies">
          <LoadingSpinner />
        </div>
        <div v-else-if="replies.length === 0" class="no-replies">
          No replies yet
        </div>
        <div v-else class="replies-list">
          <div v-for="reply in replies" :key="reply.id" class="reply">
            <div class="reply-header">
              <router-link :to="`/${reply.user_details?.username || 'unknown'}`" class="reply-profile-link">
                <img :src="reply.user_details?.avatar || 'default-avatar.png'" alt="Reply Profile" class="reply-profile-pic" />
              </router-link>
              <div class="reply-user-details">
                <p class="reply-author">
                  {{ reply.user_details?.first_name || '' }} {{ reply.user_details?.last_name || '' }}
                  <img
                    v-if="reply.user_details?.verified"
                    src="@/assets/verified.png"
                    alt="Verified"
                    class="verified-badge"
                    title="Verified Skier"
                  />
                </p>
              </div>
              <p class="reply-date">{{ formatDate(reply.created) }}</p>
              <i 
                v-if="reply.user_details?.id === userID && isAuthenticated" 
                class="fas fa-trash delete-icon" 
                @click="confirmReplyDelete(reply.id)" 
                title="Delete Reply"
              ></i>
            </div>
            <p class="reply-body">{{ reply.body }}</p>
          </div>
        </div>
        
        <!-- New Reply Form -->
        <div v-if="isAuthenticated" class="new-reply-form">
          <textarea
            v-model="newReply" 
            placeholder="Reply to this tip" 
            @keyup.enter="createReply"
            rows="1"
            class="reply-textarea"
          ></textarea>
          <button 
            @click="createReply"
            @mousedown.prevent
            :disabled="creatingReply"
          >
            <span v-if="!creatingReply">Send</span>
            <LoadingSpinner v-else class="button-spinner" />
          </button>
        </div>
        
        <!-- Login prompt for non-authenticated users -->
        <div v-else class="login-prompt">
          <p>Please <span class="login-link" @click="redirectToLogin">login</span> to reply to this tip.</p>
        </div>
      </div>
    </div>
    
    <div v-else class="not-found">
      <p>Tip not found or has been deleted.</p>
    </div>

    <!-- Pro Tip Modal -->
    <div v-if="showProTipModal" class="modal-overlay" @click="showProTipModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>PRO TIP</h3>
          <button class="modal-close" @click="showProTipModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <p>This is a paid pro tip. Only the skier who paid for this tip (and the pro who created the tip) can read more than 200 characters.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from 'axios';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

export default {
  name: 'TipDetail',
  components: {
    LoadingSpinner
  },
  data() {
    return {
      tip: null,
      replies: [],
      loading: true,
      loadingReplies: false,
      userID: null,
      userVotes: {},
      newReply: '',
      creatingReply: false,
      showProTipModal: false,
      postCreatorId: null,
      showReplies: false,
      isAuthenticated: false
    };
  },
  computed: {
    tipId() {
      return this.$route.params.id;
    },
    shouldBlurContent() {


      // Only blur if it's a pro tip with >200 chars AND user is neither author nor post creator
      return this.tip?.pro_tip && 
             this.tip?.body?.length > 200 && 
             this.tip?.user?.user_id !== this.userID && 
             this.postCreatorId !== this.userID;
    }
  },
  
  created() {
    this.checkAuthentication();
    this.fetchTipDetail();
    if (this.isAuthenticated) {
      this.getUserID();
    }
  },
  methods: {
    async fetchTipDetail() {
      this.loading = true;
      try {
        // Fetch tip details without authentication - public endpoint
        const headers = {};
        if (this.isAuthenticated) {
          headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
        }

        const response = await axios.get(`${API_BASE_URL}/api/comment-detail/${this.tipId}/`, {
          headers
        });

        this.tip = {
          ...response.data,
          userVote: 0,
          reply_count: response.data.reply_count || 0
        };

        // Fetch the post creator ID if post ID is available
        if (response.data.post) {
          await this.fetchPostCreator(response.data.post);
        }

        // Fetch user votes and replies only if authenticated
        if (this.isAuthenticated) {
          await this.fetchUserVotes();
        }

        // Always fetch reply count
        await this.fetchReplyCount();

      } catch (error) {
        console.error('Error fetching tip details:', error);
        this.tip = null;
      } finally {
        this.loading = false;
      }
    },

    async fetchReplies() {
      this.loadingReplies = true;
      try {
        const headers = {};
        if (this.isAuthenticated) {
          headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
        }

        const response = await axios.get(`${API_BASE_URL}/api/comment-replies/${this.tipId}/`, {
          headers
        });
        this.replies = response.data;
      } catch (error) {
        console.error('Error fetching replies:', error);
        this.replies = [];
      } finally {
        this.loadingReplies = false;
      }
    },

    async getUserID() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.userID = response.data.id;
      } catch (error) {
        console.error('Error fetching user ID:', error);
      }
    },

    async fetchUserVotes() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/api/user-comment-rates/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.userVotes = response.data.reduce((acc, vote) => {
          acc[vote.comment] = { rate: vote.rate, rateId: vote.id };
          return acc;
        }, {});
        
        // Set the user vote for this tip
        if (this.tip && this.userVotes[this.tip.id]) {
          this.tip.userVote = this.userVotes[this.tip.id].rate;
        }
      } catch (error) {
        console.error('Error fetching user votes:', error);
      }
    },

    async fetchReplyCount() {
      try {
        const headers = {};
        if (this.isAuthenticated) {
          headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
        }

        const response = await axios.get(`${API_BASE_URL}/api/comment-reply-count/${this.tipId}/`, {
          headers
        });
        
        if (this.tip) {
          this.tip.reply_count = response.data.count;
        }
      } catch (error) {
        console.error('Error fetching reply count:', error);
        // If there's an error, keep the existing reply count or default to 0
        if (this.tip && !this.tip.reply_count) {
          this.tip.reply_count = 0;
        }
      }
    },

    async handleVote(value) {
      if (!this.isAuthenticated) {
        this.redirectToLogin();
        return;
      }

      if (!this.tip) return;
      
      const currentVote = this.tip.userVote || 0;

      try {
        const token = localStorage.getItem('access_token');
        const rateId = this.userVotes[this.tip.id]?.rateId;

        if (currentVote !== 0 && currentVote !== value) {
          await axios.delete(`${API_BASE_URL}/api/rate-delete/${rateId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.updateTipVotes(currentVote, 0);
        }

        if (currentVote !== value) {
          const requestData = { comment: this.tip.id, rate: value, user: this.userID };
          let response;

          if (rateId && currentVote !== 0) {
            response = await axios.post(
              `${API_BASE_URL}/api/rate-update/${rateId}/`,
              requestData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          } else {
            response = await axios.post(
              `${API_BASE_URL}/api/rate-create/`,
              requestData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          }

          this.userVotes[this.tip.id] = {
            rateId: response.data.id,
            rate: value,
          };
          this.updateTipVotes(0, value);
        } else if (currentVote === value) {
          await axios.delete(`${API_BASE_URL}/api/rate-delete/${rateId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.updateTipVotes(currentVote, 0);
        }
      } catch (error) {
        console.error('Error handling vote:', error);
      }
    },

    updateTipVotes(currentVote, newVote) {
      if (newVote === 0) {
        this.tip.ratecount -= currentVote;
        this.tip.userVote = 0;
        delete this.userVotes[this.tip.id];
      } else {
        this.tip.ratecount += newVote - currentVote;
        this.tip.userVote = newVote;
        this.userVotes[this.tip.id] = { ...this.userVotes[this.tip.id], rate: newVote };
      }
    },

    async createReply() {
      if (!this.isAuthenticated) {
        this.redirectToLogin();
        return;
      }

      if (!this.newReply.trim() || this.creatingReply) return;

      this.creatingReply = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(
          `${API_BASE_URL}/api/reply-create/`,
          {
            comment: this.tipId,
            body: this.newReply,
            user: this.userID,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.id) {
          this.newReply = '';
          this.replies.push(response.data);
          this.tip.reply_count = (this.tip.reply_count || 0) + 1;
        }
      } catch (error) {
        console.error('Error creating reply:', error);
      } finally {
        this.creatingReply = false;
      }
    },

    async confirmDelete() {
      if (!this.isAuthenticated) {
        this.redirectToLogin();
        return;
      }

      if (confirm('Are you sure you want to delete this tip?')) {
        try {
          const token = localStorage.getItem('access_token');
          await axios.delete(`${API_BASE_URL}/api/comment-delete/${this.tipId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          // Navigate back after deletion
          this.$router.push('/');
        } catch (error) {
          console.error('Error deleting tip:', error);
        }
      }
    },

    async confirmReplyDelete(replyId) {
      if (!this.isAuthenticated) {
        this.redirectToLogin();
        return;
      }

      if (confirm('Are you sure you want to delete this reply?')) {
        try {
          const token = localStorage.getItem('access_token');
          await axios.delete(`${API_BASE_URL}/api/reply-delete/${replyId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.replies = this.replies.filter(reply => reply.id !== replyId);
          this.tip.reply_count = Math.max(0, (this.tip.reply_count || 0) - 1);
        } catch (error) {
          console.error('Error deleting reply:', error);
        }
      }
    },

    formatDate(dateString) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    toggleReplies() {
      this.showReplies = !this.showReplies;

      if (this.showReplies && this.replies.length === 0) {
        this.fetchReplies();
      }
    },

    checkAuthentication() {
      this.isAuthenticated = localStorage.getItem('access_token') !== null;
    },

    redirectToLogin() {
      this.$router.push('/login');
    },

    async fetchPostCreator(postId) {
      try {
        const headers = {};
        if (this.isAuthenticated) {
          headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
        }

        const response = await axios.get(`${API_BASE_URL}/api/post-detail/${postId}/`, {
          headers
        });
        
        this.postCreatorId = response.data.author?.id;
      } catch (error) {
        console.error('Error fetching post creator:', error);
        this.postCreatorId = null;
      }
    }
  }
};
</script>

<style scoped>
.tip-detail-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 0px 56px 0px;
  background-color: #15202b;
}

.tip-detail {
  display: flex;
  flex-direction: column;
}

.tip-card {
  padding: 16px;
  border-bottom: 1px solid #38444d;
  background-color: #192734;
  border-radius: 8px;
  margin-bottom: 20px;
}

.tip-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.tip-profile-link {
  text-decoration: none;
}

.tip-profile-pic {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
}

.tip-user-details {
  display: flex;
  flex-direction: column;
  gap: 0px;
}

.tip-author {
  font-weight: bold;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 1.1em;
}

.tip-full-name {
  font-size: 0.9em;
  color: #8899a6;
  margin: -2px 0 0 0;
  text-align: left;
}

.tip-date {
  font-size: 0.9em;
  color: #8899a6;
  margin-left: auto;
}

.delete-icon {
  color: #e0245e;
  cursor: pointer;
  margin-left: 10px;
  transition: color 0.2s;
}

.delete-icon:hover {
  color: #ff5a78;
}

.tip-body-container {
  position: relative;
  margin-bottom: 16px;
}

.tip-body {
  color: white;
  text-align: left;
  font-size: 1.1em;
  line-height: 1.5;
}

.visible-text {
  color: white;
}

.blurred-text {
  color: #8899a6;
  filter: blur(3px);
  -webkit-filter: blur(3px);
  opacity: 0.6;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
}

.tip-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tip-rate {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-size: 1.1em;
}

.tip-rate i {
  cursor: pointer;
  transition: color 0.2s;
  font-size: 1.2em;
}

.tip-rate .voted-up {
  color: #1da1f2;
}

.tip-rate .voted-down {
  color: #e0245e;
}

.tip-replies {
  font-size: 0.9em;
  color: #8899a6;
  cursor: pointer;
  transition: color 0.2s;
  margin: 0;
}

.tip-replies:hover {
  color: #1da1f2;
}

.replies-section {
  margin-top: 10px;
  padding-left: 20px;
  border-left: 2px solid #38444d;
}

.loading-replies {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.no-replies {
  text-align: center;
  padding: 20px;
  color: #8899a6;
}

.replies-list {
  margin-bottom: 20px;
}

.reply {
  padding: 8px;
  border-bottom: 1px solid #38444d;
  background-color: #15202b;
  border-radius: 8px;
  margin-bottom: 8px;
  text-align: left;
}

.reply-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.reply-profile-link {
  text-decoration: none;
}

.reply-profile-pic {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 8px;
}

.reply-user-details {
  display: flex;
  flex-direction: column;
  gap: 0px;
}

.reply-author {
  font-weight: bold;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.reply-date {
  font-size: 0.8em;
  color: #8899a6;
  margin-left: auto;
}

.reply-body {
  color: white;
  text-align: left;
  margin: 0;
  line-height: 1.4;
}

.new-reply-form {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.new-reply-form .reply-textarea {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  flex-grow: 1;
  padding: 6px 10px;
  border-radius: 15px;
  border: 1px solid #38444d;
  background-color: #192734;
  color: #e1e8ed;
  margin-right: 8px;
  font-size: 16px;
  height: 20px;
  transition: height 0.3s ease;
  resize: none;
  text-align: left;
  line-height: normal;
}

.new-reply-form .reply-textarea:focus {
  height: 60px;
}

.new-reply-form button {
  padding: 6px 12px;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  height: 32px;
}

.new-reply-form button:hover:not(:disabled) {
  background-color: #0c8ddb;
}

.new-reply-form button:disabled {
  background-color: #38444d;
  cursor: not-allowed;
}

.button-spinner {
  width: 16px;
  height: 16px;
}

.verified-badge {
  width: 20px;
  height: 20px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

.pro-tip-badge {
  background-color: #1da1f2;
  color: #ffffff;
  font-size: 0.7em;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
  margin-left: 12px;
  display: inline-block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.pro-tip-badge:hover {
  background-color: #0c8ddb;
}

.not-found {
  text-align: center;
  padding: 40px 20px;
  color: #8899a6;
  font-size: 1.2em;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #192734;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  margin: 20px;
  border: 1px solid #38444d;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #38444d;
}

.modal-header h3 {
  color: #1da1f2;
  font-size: 1.1em;
  font-weight: bold;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-close {
  background: none;
  border: none;
  color: #8899a6;
  font-size: 1.5em;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #ffffff;
}

.modal-body {
  padding: 20px;
}

.modal-body p {
  color: #e1e8ed;
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

@media screen and (max-width: 600px) {
  .tip-detail-container {
    padding: 20px 4px 100px 4px;
  }
}

.login-prompt {
  text-align: center;
  padding: 20px;
  color: #8899a6;
}

.login-link {
  color: #1da1f2;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;
}

.login-link:hover {
  color: #0c8ddb;
}
</style> 